import _ from 'lodash'
import * as R from 'ramda'
import React, { useMemo, useRef, useState } from 'react'
import { Button } from 'semantic-ui-react'

import { ACCOUNTS_URL } from 'domains/user'
import useAxios, { mutate } from 'hooks/useAxios'
import useModal from 'hooks/useModal'
import { AllowPermission } from 'shared/components/AllowPermission'
import { toast } from 'libs/toast'
import { UserForm } from 'organisms/UserForm'
import usePermission from 'hooks/usePermission'
import PageHeaderContainer from 'shared/components/PageHeader/container'
import { LimitFeatureButton } from 'components/LimitFeatureButton'
import { CrownBadge } from 'components/ProFeatureLabel'
import constants from 'config/constants'
import { useNavbarMenuContext } from 'organisms/NavBar/context'
import useSaveUserActivityLog, { ACCOUNT_ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import useUser from 'hooks/useUser'
import { isValidCustomLoginMethod } from 'libs/user'

export default function InviteUserContainer({
  totalAccount,
  roleOptions,
  surveyAccessSelectableRoles,
  dashbardAccessesSelectableRoles,
}) {
  const { Modal, open, close } = useModal()
  const userInfo = useUser()
  const companyId = R.path(['selected_workspace', 'id'], userInfo)

  /** @type {[*, function]} */
  const [userState, setUserState] = useState({
    role: 'admin',
    surveys: [],
    dashboards: [],
    disable_sso: false,
    is_custom_login: false,
    login_methods: [],
  })
  const [email, setEmail] = useState(null)
  const [emailError, setEmailError] = useState('')
  const { user: loggedInUser, hasPermission } = usePermission()
  const featureLimit = R.path(
    ['subscription', 'subscription_items_metadata', 'users'],
    loggedInUser
  )
  const saveUserActivityLog = useSaveUserActivityLog()

  const context = useNavbarMenuContext()
  const hiddenRefButton = useRef(null)

  const { data: untransformedSurveys, loading: surveyLoading } = useAxios({
    url: 'account/survey/?limit=5000&order_by=asc',
  })

  const untransformedDashboards = R.pathOr(
    [],
    ['contextData', 'customDashboards', 'results'],
    context
  )

  const surveys = useMemo(() => {
    if (untransformedSurveys) {
      return untransformedSurveys.results.map((data) => {
        const { id: survey_id, token, name } = data.survey
        return {
          survey_id,
          survey_token: token,
          name,
          has_permission: false,
        }
      })
    } else {
      return untransformedSurveys
    }
  }, [untransformedSurveys])

  const dashboards = useMemo(() => {
    if (untransformedDashboards) {
      return untransformedDashboards.map((data) => {
        const { id: dashboard_id, token, name } = data
        return {
          dashboard_id,
          token: token,
          name,
          has_permission: false,
        }
      })
    } else {
      return untransformedDashboards
    }
  }, [untransformedDashboards])

  const { fetch: callInviteUser, loading: inviteUserLoading } = useAxios({
    url: 'account/invitation/',
    method: 'post',
    skip: true,
  })

  const { data: enabledSSO } = useAxios({
    url: 'billing/plans/feature-toggle/',
    method: 'post',
    data: { feature: 'sso' },
  })

  const buttonLoading = inviteUserLoading || (surveyLoading && !untransformedSurveys)

  function handleSubmit() {
    const surveyAccesses = userState.surveys || []
    const dashboardAccesses = userState.dashboards || []

    const surveyTokens = surveyAccesses.map((surveyAccess) => surveyAccess.survey_token)
    const dashboardTokens = dashboardAccesses
      .filter((dashboardAccess) => dashboardAccess.has_permission)
      .map((dashboardAccess) => dashboardAccess.token)

    inviteUser(email, surveyTokens, dashboardTokens)
  }

  function inviteUser(email, surveyTokens, dashboardTokens) {
    if (!isValidCustomLoginMethod(userState.is_custom_login, userState.login_methods)) {
      toast.error('At least one login method is required')
      return
    }

    const data = {
      email: email,
      role: userState.role,
      is_custom_login: userState.is_custom_login,
      login_methods: userState.login_methods,
      survey_access: surveyTokens,
      dashboard_access: dashboardTokens,
      disable_sso: userState.disable_sso,
    }

    callInviteUser({
      data: data,
    })
      .then((res) => {
        toast.success(res.data.detail)
        close()
        mutate(
          ACCOUNTS_URL,
          (state) => {
            return {
              ...state,
              count: state.count + 1,
              results: [
                ...state.results,
                {
                  user: {
                    first_name: email,
                    email: email,
                  },
                  role: _.capitalize(userState.role),
                },
              ],
            }
          },
          true
        )
        saveUserActivityLog({
          action: ACCOUNT_ACTION_NAME.invited_user,
          statusCode: res.status,
          requestBody: data,
          target: `com_${companyId}`,
        })
      })
      .catch((error) => {
        saveUserActivityLog({
          action: ACCOUNT_ACTION_NAME.invited_user,
          statusCode: error.response.status,
          requestBody: data,
          errorResponseBody: error.response.data,
          target: `com_${companyId}`,
        })

        if (R.path(['response', 'data', 'detail'], error)) {
          setEmailError('This email already exists. Use a different one to continue.')
        } else if (R.path(['response', 'data', 'email'], error)) {
          setEmailError('Email must be a valid email address.')
        } else {
          toast.error('Something went wrong, please try again.')
        }
        console.error(error)
      })
  }

  return (
    <PageHeaderContainer
      title="Users"
      rightSlot={
        hasPermission(constants.permissions.manageUsers) && (
          <React.Fragment>
            <InviteUserButton
              handleOpen={open}
              totalAccount={totalAccount}
              featureLimit={featureLimit}
              loading={buttonLoading}
            />
            <Modal size={'tiny'}>
              <Modal.Header>Invite a new user</Modal.Header>
              <Modal.Content>
                <div className="description">
                  <UserForm
                    defaultUser={userState}
                    isInviteUser={true}
                    defaultDisableSSO={userState.disable_sso}
                    defaultSurveyAccesses={surveys}
                    defaultDashbardAccesses={dashboards}
                    loading={!surveys}
                    onChange={setUserState}
                    roleOptions={roleOptions}
                    surveyAccessSelectableRoles={surveyAccessSelectableRoles}
                    dashbardAccessesSelectableRoles={dashbardAccessesSelectableRoles}
                    showSSOEnforcementForm={
                      R.path(['enabled'], enabledSSO) &&
                      loggedInUser.workos_sso_connection_status === 'active'
                    }
                    setEmail={setEmail}
                    hiddenRefButton={hiddenRefButton}
                    handleSubmit={handleSubmit}
                    emailError={emailError}
                    onEmailErrorChange={setEmailError}
                  />
                </div>
              </Modal.Content>
              <Modal.Actions>
                <Button
                  onClick={() => {
                    close()
                    setEmailError('')
                  }}
                  content="Cancel"
                />
                <Button
                  primary
                  disabled={buttonLoading}
                  loading={buttonLoading}
                  onClick={() => hiddenRefButton.current.click()}
                >
                  <i aria-hidden="true" className="icon far fa-plus" />
                  Invite user
                </Button>
              </Modal.Actions>
            </Modal>
          </React.Fragment>
        )
      }
    />
  )
}

function InviteUserButton({ handleOpen, totalAccount, featureLimit, loading }) {
  return (
    <AllowPermission permission={constants.permissions.manageUsers}>
      <LimitFeatureButton
        featureLimit={featureLimit}
        usageCount={totalAccount}
        handleClick={handleOpen}
        descriptionTitle={
          <span className="flex items-center">
            User limit reached <CrownBadge />
          </span>
        }
        descriptionContent={
          "You've reached your account's user limit. Please remove inactive users or upgrade your plan to add more."
        }
        learnMoreLink={
          'https://help.simplesat.io/en/articles/2361167-users-permissions#inviting-a-user'
        }
        buttonText={'Invite user'}
        loading={loading}
      />
    </AllowPermission>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/InviteUser/container.js